import React, { useState } from 'react'
import PropTypes from 'prop-types'
import {
  Box,
  Typography,
  IconButton,
  Collapse,
  Paper,
  Grid
} from '@mui/material'
import AssessmentIcon from '@mui/icons-material/Assessment'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'

import { colors } from '../../consts'

import styles from './DispositionExplainer.module.scss'

/**
 * DispositionExplainer component - displays a collapsible section explaining various call disposition types
 *
 * @param {Object} props - Component props
 * @param {string} props.title - Title for the disposition explainer
 * @param {boolean} props.initiallyExpanded - Whether the component should be expanded by default
 * @param {function} props.onToggle - Callback function when the component is expanded/collapsed
 */
const DispositionExplainer = ({
  title = 'Definitions of Call Types',
  initiallyExpanded = false,
  onToggle = () => {},
  disabled = false
}) => {
  const [expanded, setExpanded] = useState(initiallyExpanded)

  const handleToggle = () => {
    if (!disabled) {
      const newExpandedState = !expanded
      setExpanded(newExpandedState)
      onToggle(newExpandedState)
    }
  }

  const dispositionItems = [
    {
      type: 'Message Taken',
      color: colors.ui.lightBlue,
      description: "The Engagement Specialist captured the caller's details and reason for calling so staff can return the call."
    },
    {
      type: 'Abandoned',
      color: colors.status.error,
      description: 'The caller hung up or the line disconnected before the Engagement Specialist could pick up the call.'
    },
    {
      type: 'Solicitation – NCN',
      color: colors.ui.lightBlue,
      description: 'A sales or marketing call deemed non-client-related (No call Note). Includes things like spam calls from telemarketers.'
    },
    {
      type: 'Not Relevant – NCN',
      color: colors.ui.neutralGray,
      description: "Call content was unrelated to the client's services. No Call Note (NCN). Includes prank calls or inquiries intended for a different business."
    },
    {
      type: 'Transferred Call',
      color: colors.ui.brown,
      description: 'The Engagement Specialist successfully routed the caller to the requested staff member or extension.',
      isBorderBottom: false
    },
    {
      type: 'Voicemail',
      color: colors.ui.teal,
      description: 'The caller was sent to voicemail, leaving (or having the option to leave) a recorded message.'
    },
    {
      type: 'Appointment Booked',
      color: colors.status.success,
      description: 'A meeting or in-office visit was scheduled during the call.'
    },
    {
      type: 'Potential New Client',
      color: colors.ui.pink,
      description: "The caller expressed interest in becoming a client or inquired about the client's services."
    },
    {
      type: 'Caller Question Answered',
      color: colors.ui.tealPrimary,
      description: "The Engagement Specialist resolved the caller's inquiry on the spot, requiring no further follow-up."
    },
    {
      type: 'Urgent Call',
      color: colors.status.error,
      description: 'A time sensitive call that needs immediate follow-up by the client.',
      isHighlighted: true,
      isBorderBottom: false
    }
  ]

  return (
    <Box className={styles.container}>
      <Box
        className={`${styles.headerBox} ${disabled ? styles.disabled : ''}`}
        onClick={handleToggle}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            handleToggle()
          }
        }}
        role='button'
        tabIndex={0}
        aria-expanded={expanded}
      >
        <Box className={styles.titleBox}>
          <AssessmentIcon className={styles.headerIcon} />
          <Typography className={styles.headerTitle}>
            {title}
          </Typography>
        </Box>
        <Box className={`${styles.expandButton} ${expanded ? styles.expanded : ''}`}>
          <Typography
            variant='body2'
            className={styles.expandText}
          >
            {expanded ? 'Hide' : 'Show'} Definitions
          </Typography>
          <IconButton
            size='small'
            className={styles.expandIcon}
            disabled={disabled}
            aria-label={expanded ? 'Hide definitions' : 'Show definitions'}
            onClick={(e) => {
              e.stopPropagation()
              handleToggle()
            }}
          >
            {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </Box>
      </Box>

      <Collapse in={expanded} timeout='auto' unmountOnExit>
        <Paper className={styles.contentPaper}>
          <Box className={styles.contentHeader}>
            <Typography variant='h6' className={styles.contentTitle}>
              Call Types
            </Typography>
            <Typography variant='body2' className={styles.contentSubtitle}>
              These categories help classify and track the outcome of each call
            </Typography>
          </Box>

          <Grid container spacing={0}>
            <Grid item xs={12} sm={6}>
              {/* First column of disposition items */}
              {dispositionItems.slice(0, 5).map((item, index) => (
                <DispositionItem
                  key={`col1-${index}`}
                  type={item.type}
                  color={item.color}
                  description={item.description}
                  isHighlighted={item.isHighlighted || false}
                  isBorderBottom={item.isBorderBottom !== false}
                />
              ))}
            </Grid>

            <Grid item xs={12} sm={6} className={styles.rightColumn}>
              {/* Second column of disposition items */}
              {dispositionItems.slice(5).map((item, index) => (
                <DispositionItem
                  key={`col2-${index}`}
                  type={item.type}
                  color={item.color}
                  description={item.description}
                  isHighlighted={item.isHighlighted || false}
                  isBorderBottom={item.isBorderBottom !== false}
                />
              ))}
            </Grid>
          </Grid>
        </Paper>
      </Collapse>
    </Box>
  )
}

DispositionExplainer.propTypes = {
  title: PropTypes.string,
  initiallyExpanded: PropTypes.bool,
  onToggle: PropTypes.func,
  disabled: PropTypes.bool
}

/**
 * DispositionItem - Individual disposition type item
 */
const DispositionItem = ({
  type,
  color,
  description,
  isHighlighted = false,
  isBorderBottom = true
}) => {
  return (
    <Box className={`${styles.dispositionContainer} ${isBorderBottom ? styles.withBorder : ''}`}>
      <Box className={styles.dispositionContent}>
        <Box
          className={`${styles.dispositionType} ${isHighlighted ? styles.highlighted : ''}`}
          sx={{ backgroundColor: color }}
        >
          {type}
        </Box>
        <Typography className={styles.dispositionDescription}>
          {description}
        </Typography>
      </Box>
    </Box>
  )
}

DispositionItem.propTypes = {
  type: PropTypes.string.isRequired,
  color: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  isHighlighted: PropTypes.bool,
  isBorderBottom: PropTypes.bool
}

export default DispositionExplainer
